/**
 * The line above the Integrations list naming this Engage environment and
 * saying that only its registered connections are listed. The environment
 * comes from GATSBY_ENV; each frontend deploy talks to the backend of the same
 * environment, whose zone prefix does the filtering.
 */
import { Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { environment } from '@constants/env';

const messages = defineMessages({
  production: {
    id: 'ehrSync.list.environment.production',
    defaultMessage: 'Production environment',
  },
  staging: {
    id: 'ehrSync.list.environment.staging',
    defaultMessage: 'Staging environment',
  },
  development: {
    id: 'ehrSync.list.environment.development',
    defaultMessage: 'Dev environment',
  },
  scope: {
    id: 'ehrSync.list.environment.scope',
    defaultMessage:
      'Showing the {count, plural, one {# connection} other {# connections}} registered for this Engage environment. Zones from other environments are hidden.',
  },
});

/** Props for {@link EnvironmentBanner}. */
interface EnvironmentBannerProps {
  /** Connections listed, for the sentence. */
  count: number;
  /** Right-hand action, e.g. the Register connection button. */
  action?: React.ReactNode;
}

/**
 * Environment badge, scope sentence and an optional action.
 *
 * @param props - see {@link EnvironmentBannerProps}
 */
export default function EnvironmentBanner({
  count,
  action,
}: EnvironmentBannerProps) {
  const { formatMessage } = useIntl();
  const label =
    environment === 'production'
      ? messages.production
      : environment === 'staging'
        ? messages.staging
        : messages.development;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ sm: 'center' }}
      sx={{ mb: 2 }}
    >
      <Typography
        component="span"
        variant="caption"
        fontWeight="bold"
        sx={(theme) => ({
          px: 1.1,
          py: 0.4,
          borderRadius: 999,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          color: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          alignSelf: { xs: 'flex-start', sm: 'center' },
        })}
      >
        {formatMessage(label)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {formatMessage(messages.scope, { count })}
      </Typography>
      {action}
    </Stack>
  );
}
